import { Box, Radio, Stack } from '@mui/joy';
import { IAfterGuiAttachedParams } from 'ag-grid-community';
import { CustomFilterProps, useGridFilter } from 'ag-grid-react';
import { ChangeEvent, useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Autocomplete, Button, IAutocompleteProps } from 'core/components';
import { EMPTY, NON_EMPTY } from 'core/constants';
import { IAutocompleteOption } from 'core/types';
import { Size } from 'theme/enums';

interface IProps extends CustomFilterProps {
  autocompleteProps: IAutocompleteProps;
  getAutocompleteValue?: (option: IAutocompleteOption) => string;
  labelEmpty?: string;
  labelNonEmpty?: string;
}

export const AutocompleteFilter = ({
  model,
  onModelChange,
  autocompleteProps,
  getAutocompleteValue = (option: IAutocompleteOption) => option.id,
  labelEmpty,
  labelNonEmpty,
}: IProps) => {
  const [checkedEmpty, setCheckedEmpty] = useState(model?.filter === '(empty)' || false);
  const [checkedNonEmpty, setCheckedNonEmpty] = useState(model?.filter === '(all)' || false);
  const [autocompleteValue, setAutocompleteValue] = useState<IAutocompleteOption | null>(null);

  const popupHideRef = useRef<(() => void) | null>(null);

  const { t } = useTranslation();

  const doesFilterPass = useCallback(() => true, []);

  const afterGuiAttached = useCallback((params?: IAfterGuiAttachedParams) => {
    if (params.hidePopup) {
      popupHideRef.current = params?.hidePopup;
    }
  }, []);

  useGridFilter({
    doesFilterPass,
    afterGuiAttached,
  });

  const handleChange = (_: ChangeEvent<HTMLInputElement>, option: IAutocompleteOption) => {
    setCheckedEmpty(false);
    setCheckedNonEmpty(false);

    setAutocompleteValue(option);
  };

  const onApply = () => {
    if (!autocompleteValue && !checkedEmpty && !checkedNonEmpty) {
      if (model?.filter) {
        onModelChange(null);
      }

      return;
    }

    onModelChange({
      filter: checkedEmpty
        ? EMPTY
        : checkedNonEmpty
        ? NON_EMPTY
        : getAutocompleteValue(autocompleteValue),
    });

    popupHideRef.current?.();
  };

  const onReset = () => {
    setCheckedEmpty(false);
    setCheckedNonEmpty(false);
    setAutocompleteValue(null);
    onModelChange(null);

    popupHideRef.current?.();
  };

  return (
    <Box
      className="ag-filter"
      onMouseDown={(e) => {
        // Prevent AG Grid from closing the popup when interacting with the dropdown
        e.stopPropagation();
      }}
    >
      <Box className="ag-filter-body-wrapper ag-simple-filter-body-wrapper">
        <Stack gap={1}>
          <Radio
            name="emptyFilter"
            size={Size.SM}
            label={labelEmpty || t('labelEmpty')}
            checked={checkedEmpty}
            onChange={() => {
              setCheckedEmpty(true);
              setCheckedNonEmpty(false);
            }}
          />

          <Radio
            name="nonEmptyFilter"
            size={Size.SM}
            label={labelNonEmpty || t('labelNonEmpty')}
            checked={checkedNonEmpty}
            onChange={() => {
              setCheckedNonEmpty(true);
              setCheckedEmpty(false);
            }}
          />

          <Autocomplete
            name="autocompleteFilter"
            value={
              autocompleteValue ||
              (model?.filter !== EMPTY && model?.filter !== NON_EMPTY ? model?.filter : null)
            }
            onChange={handleChange as any}
            placeholder={t('placeholderSelectOption')}
            {...autocompleteProps}
          />
        </Stack>
      </Box>
      <Box className="ag-filter-apply-panel">
        <Button
          text={t('buttonLabelReset')}
          className="ag-button ag-standard-button ag-filter-apply-panel-button"
          onClick={onReset}
          sx={{
            color: 'inherit',
            fontSize: 'inherit',
            fontWeight: 'inherit',
          }}
        />
        <Button
          text={t('buttonLabelApply')}
          className="ag-button ag-standard-button ag-filter-apply-panel-button"
          onClick={onApply}
          sx={{
            color: 'inherit',
            fontSize: 'inherit',
            fontWeight: 'inherit',
          }}
        />
      </Box>
    </Box>
  );
};
